import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { FaBox, FaCheck, FaMapMarkerAlt } from "react-icons/fa";
import classes from "./Order.module.css";

const OrderPreviewDialog = ({
  isOpen,
  onClose,
  onSubmit,
  orderNo,
  circularName,
  rows = [],
  products = [],
  shippingAddresses = [],
  isSubmitting = false,
}) => {
  // Lock background scroll while the dialog is open (important on mobile)
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getProductName = (productId) => {
    const product = products.find((p) => p.id == productId);
    return product ? product.tally_name || product.name : "Unknown Product";
  };

  // Tally stores multi-line addresses joined with "|||"
  const cleanAddress = (text) =>
    text
      .replaceAll("|||", ", ")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .join(", ");

  const getAddressLabel = (row) => {
    const address = shippingAddresses.find(
      (option) => option.value == row.shipping_address_id,
    );
    if (address) return cleanAddress(address.label);
    const parts = [
      row.shipping_address,
      row.shipping_city,
      row.shipping_state,
      row.shipping_zip,
    ].filter(Boolean);
    return parts.length > 0
      ? cleanAddress(parts.join(", "))
      : "Address not selected";
  };

  // Group rows by shipping address, keeping the order in which addresses first appear
  const addressGroups = [];
  rows.forEach((row) => {
    const key = String(row.shipping_address_id ?? "");
    let group = addressGroups.find((g) => g.key === key);
    if (!group) {
      group = { key, label: getAddressLabel(row), items: [] };
      addressGroups.push(group);
    }
    group.items.push(row);
  });

  return ReactDOM.createPortal(
    <div
      className={`${classes.dialogOverlay} ${classes.previewOverlay}`}
      onClick={onClose}
    >
      <div
        className={`${classes.dialogContainer} ${classes.previewContainer}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.dialogHeader}>
          <div>
            <h3 className={classes.dialogTitle}>
              <FaBox className="me-2 mr-2" />
              {orderNo ? `Order Preview - #${orderNo}` : "Order Preview"}
            </h3>
            {circularName ? (
              <div className={classes["circular-message"]}>
                Circular applied:- {circularName}
              </div>
            ) : (
              <div className={classes["warning-message"]}>
                No circular applied
              </div>
            )}
          </div>
          <button
            type="button"
            className={classes.closeButton}
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className={classes.dialogContent}>
          {addressGroups.length > 0 ? (
            addressGroups.map((group, groupIndex) => (
              <div
                key={group.key || groupIndex}
                className={groupIndex > 0 ? "mt-4" : ""}
              >
                <div className={classes.previewAddress}>
                  <FaMapMarkerAlt className="mr-2 me-2" />
                  <span>{group.label}</span>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-striped mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "40px" }}>#</th>
                        <th>Product</th>
                        <th style={{ whiteSpace: "nowrap" }}>
                          Qty
                        </th>
                        <th style={{ whiteSpace: "nowrap" }}>
                          Unit Price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td style={{ wordBreak: "break-word" }}>
                            {getProductName(item.stock_id)}
                          </td>
                          <td>{item.qty || 0}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            ₹{parseFloat(item.price || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center">No products found</div>
          )}
        </div>

        <div className={classes.dialogFooter}>
          <button
            type="button"
            className={classes.cancelButton}
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className={classes.confirmButton}
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            <FaCheck className="me-2 mr-2" />
            Submit
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OrderPreviewDialog;
